'use client';

import classNames from 'classnames';
import React, { useCallback, useContext } from 'react';
import { useTranslation } from 'react-i18next';

import Image from 'next/image';

import { PropsWithClassname } from '@/app/types';
import { SearchBarContext } from '@/app/providers/SearchBarProvider';

import illustration from '@/public/public/images/not-found-illustration.svg';

import Button, { ButtonHTMLTag, ButtonSize, ButtonStyle } from '@/app/components/Button/Button';
import LensIcon from '@/app/components/LensIcon/LensIcon';

interface NotFoundWidgetProps extends PropsWithClassname {}

const NotFoundSplash = ({ className }: NotFoundWidgetProps) => {
  const { t } = useTranslation();
  const { setIsSearchBarOpen } = useContext(SearchBarContext);

  const handleSearchButtonClick = useCallback(() => {
    setIsSearchBarOpen(true);
  }, [setIsSearchBarOpen]);

  return (
    <div className={classNames(className, 'w-full px-[24px] text-center')}>
      <Image
        width={280}
        height={290}
        src={illustration}
        alt={t('components.NotFoundSplash.illustrationAlt')}
        className="inline-block w-[280px] h-auto rounded-md mb-[40px]"
      />
      <h1 className="text-[32px] leading-[32px] lg:text-[48px] lg:leading-[48px] mb-[32px]">
        {t('components.NotFoundSplash.heading')}
      </h1>
      <p className="mb-[16px]">{t('components.NotFoundSplash.text')}</p>
      <Button
        style={ButtonStyle.PRIMARY_OUTLINED}
        size={ButtonSize.LARGE}
        tagSpecificProps={{ htmlTag: ButtonHTMLTag.BUTTON, onClick: handleSearchButtonClick }}
        className="inline-flex items-center"
      >
        <LensIcon className="mr-[16px]" />
        <span className="tracking-[2px]">{t('components.NotFoundSplash.buttonText')}</span>
      </Button>
    </div>
  );
};

export default NotFoundSplash;
